import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Chip, Button, IconButton, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack, Edit, PictureAsPdf, FileDownload, ContentCopy,
  LocalShipping, Business, Inventory2, Receipt,
} from '@mui/icons-material';
import api from '../../services/api';
import { buildShippingInvoiceXLSX } from './buildShippingInvoiceXLSX';

const FWD_LABEL = { fedex_india: 'FedEx India A/C — 651730414', yitong_jimmy: 'Yitong / Jimmy', fedex_china: 'FedEx China', self: 'Self', other: 'Other' };
const STATUS_COLORS = { draft: 'default', ready: 'info', shipped: 'primary', received: 'success', cancelled: 'error' };

const Field = ({ label, value, mono }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography variant="caption" color="text.secondary"
      sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.3, fontFamily: mono ? 'monospace' : undefined, whiteSpace: 'pre-wrap' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const fmtMoney = (v, ccy) => v == null ? '—' : `${ccy || ''} ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
const fmtPrice = (v, ccy) => v == null ? '—' : `${ccy || ''} ${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`.trim();
const fmtQty   = (v) => v == null ? '—' : Math.round(parseFloat(v)).toLocaleString('en-US');

export default function ShippingInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [si, setSi]       = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/shipping-invoices/${id}`);
      const d = r.data.data || {};
      setSi(d); setItems(d.items || []);
    } catch (e) { setError(e.response?.data?.message || 'Failed to load'); }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openPdf = async () => {
    try {
      const r = await api.get(`/shipping-invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { alert('Failed to open PDF'); }
  };

  const downloadExcel = () => {
    if (!si) return;
    buildShippingInvoiceXLSX(si, items);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error)   return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!si)     return null;

  const totalQty = items.reduce((s, i) => s + parseFloat(i.quantity || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/shipping-invoices')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{si.invoice_number}</Typography>
            <Chip size="small" label={si.status} color={STATUS_COLORS[si.status]} variant={si.status === 'draft' ? 'outlined' : 'filled'} />
            {si.proforma_number && <Chip size="small" variant="outlined" color="primary" label={`PI: ${si.proforma_number}`}
              onClick={() => si.proforma_invoice_id && navigate(`/invoices/${si.proforma_invoice_id}`)} />}
          </Stack>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 700 }}>
            {si.beneficiary_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdf />} onClick={openPdf}>PDF</Button>
          <Button variant="outlined" size="small" startIcon={<FileDownload />} onClick={downloadExcel}>Excel</Button>
          <Button variant="outlined" color="secondary" size="small" startIcon={<ContentCopy />}
            onClick={() => navigate(`/shipping-invoices/new?clone=${id}`)}>Clone</Button>
          <Button variant="contained" size="small" startIcon={<Edit />} onClick={() => navigate(`/shipping-invoices/${id}/edit`)}>Edit</Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Receipt fontSize="small" /> Shipment Details
            </Typography>
            <Field label="Invoice No." value={si.invoice_number} mono />
            <Field label="Ship Date"   value={si.ship_date} />
            <Field label="Currency"    value={si.currency} />
            <Field label="Freight Terms" value={si.freight_terms} />
            {si.freight_value != null && <Field label="Freight Value" value={fmtMoney(si.freight_value, si.currency)} />}
            <Field label="Status"      value={si.status} />
            {si.proforma_number && <Field label="Linked Proforma" value={`${si.proforma_number}${si.proforma_total ? ` · ${si.currency} ${parseFloat(si.proforma_total).toFixed(2)}` : ''}`} mono />}
            {si.notes && <Field label="Notes" value={si.notes} />}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" /> FROM (Supplier)
            </Typography>
            <Field label="Company" value={si.beneficiary_name} />
            <Field label="Contact" value={si.sender_contact_name || si.supplier_default_contact} />
            <Field label="Phone"   value={si.sender_phone} />
            <Field label="Address" value={si.sender_address || si.supplier_address} />
            <Field label="City / Country" value={`${si.sender_city || si.supplier_city || ''}, ${si.sender_country || si.supplier_country || 'China'}`} />
            <Field label="Zip"     value={si.sender_zip} />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping fontSize="small" /> Forwarder & AWB
            </Typography>
            <Field label="Forwarder" value={FWD_LABEL[si.forwarder] || si.forwarder} />
            {si.forwarder_notes && <Field label="Forwarder Notes" value={si.forwarder_notes} />}
            <Field label="FedEx Account" value={si.fedex_account} mono />
            <Field label="FedEx AWB" value={si.fedex_awb} mono />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block', fontStyle: 'italic' }}>
              Receiver: Identium Tech Solutions Pvt Ltd · {si.receiver_contact_name} · {si.receiver_phone}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              GSTIN: 07AAGCI6275E1ZH · IEC: AAGCI6275E · AdCode: 0181391
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 fontSize="small" /> Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>HSN</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No items.</TableCell></TableRow>
                  ) : items.map((it, i) => (
                    <TableRow key={it.id} hover>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 11 }}>{i + 1}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{it.description}</Typography></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.sku || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.hsn_code || '—'}</TableCell>
                      <TableCell align="right">{fmtQty(it.quantity)}</TableCell>
                      <TableCell align="right">{fmtPrice(it.unit_price, si.currency)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(it.amount, si.currency)}</TableCell>
                    </TableRow>
                  ))}
                  {items.length > 0 && (
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover' } }}>
                      <TableCell colSpan={4}>TOTAL</TableCell>
                      <TableCell align="right">{fmtQty(totalQty)}</TableCell>
                      <TableCell align="right">Freight: {si.freight_value != null ? fmtMoney(si.freight_value, si.currency) : (si.freight_terms || 'FOB')}</TableCell>
                      <TableCell align="right">{fmtMoney(si.total_value, si.currency)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
